import React, { useState, useEffect } from 'react';
import { X, History, Trash2 } from 'lucide-react';
import ShareMenu from './ShareMenu';
import { t, getLocale } from '../utils/i18n';
import './TrackHistory.css';

const STORAGE_KEY = 'majestic_track_history';
const MAX_HISTORY = 50;

export const addToHistory = (track) => {
  if (!track || !track.title || track.title === 'Live' || track.title === 'On Air') return;

  const history = getHistory();

  // Check if this track is already the most recent
  if (history.length > 0 &&
      history[0].title === track.title &&
      history[0].artist === track.artist) {
    // Update artwork if it wasn't available before but is now
    if (!history[0].artwork && track.artwork) {
      history[0].artwork = track.artwork;
      history[0].album = track.album || history[0].album;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
    }
    return;
  }

  const newEntry = {
    id: Date.now(),
    title: track.title,
    artist: track.artist,
    album: track.album || '',
    artwork: track.artwork,
    timestamp: new Date().toISOString()
  };

  const newHistory = [newEntry, ...history].slice(0, MAX_HISTORY);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(newHistory));
};

export const getHistory = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

export const clearHistory = () => {
  localStorage.removeItem(STORAGE_KEY);
};

const TrackHistory = ({ isOpen, onClose }) => {
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setHistory(getHistory());
    }
  }, [isOpen]);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return t('timeNow');
    if (diffMins < 60) return t('timeMinAgo', { n: diffMins });
    if (diffHours < 24) return t('timeHourAgo', { n: diffHours });

    // Show localized date for older entries
    return date.toLocaleDateString(getLocale(), {
      day: 'numeric',
      month: 'short'
    });
  };

  const handleClearHistory = () => {
    if (window.confirm(t('historyClearConfirm'))) {
      clearHistory();
      setHistory([]);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="history-overlay" onClick={onClose}>
      <div className="history-panel" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="history-header">
          <div className="history-title">
            <History size={20} />
            <span>{t('historyTitle')}</span>
            <span className="history-count">({history.length})</span>
          </div>
          <div className="history-actions">
            {history.length > 0 && (
              <button
                className="history-clear-btn"
                onClick={handleClearHistory}
                title={t('historyClearTitle')}
                data-testid="history-clear-button"
              >
                <Trash2 size={20} />
              </button>
            )}
            <button
              className="history-close"
              onClick={onClose}
              data-testid="history-close"
              aria-label="Close"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="history-content">
          {history.length === 0 ? (
            <div className="history-empty">
              <History size={48} strokeWidth={1} />
              <p>{t('historyEmpty')}</p>
              <span>{t('historyEmptyHint')}</span>
            </div>
          ) : (
            <div className="history-list">
              {history.map((track) => (
                <div key={track.id} className="history-item" data-testid="history-item">
                  <div className="history-item-artwork">
                    {track.artwork ? (
                      <img src={track.artwork} alt={track.title} />
                    ) : (
                      <div className="history-item-placeholder">
                        <span>♪</span>
                      </div>
                    )}
                  </div>
                  <div className="history-item-info">
                    <h4>{track.title}</h4>
                    <p>{track.artist}</p>
                    <span className="history-item-meta">
                      {formatTime(track.timestamp)}
                      {track.album && ` • ${track.album}`}
                    </span>
                  </div>
                  <ShareMenu track={track} anchor="right" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {history.length > 0 && (
          <div className="history-footer">
            {t('historyKept', { count: history.length })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackHistory;
